// Canonical Hedge Fam API contract types — the single source of truth shared
// by the web app (web-app/, Vite) and the mobile app (mobile-app/, Expo
// Metro). Both consume this file so the wire shapes never drift between
// surfaces. These mirror the backend service response shapes (fam, vote,
// fantasy); the services emit camelCase JSON tags (e.g. `logoUrl`, `pmUserId`,
// `proposedAt`) — this file matches.
//
// web-app/src/lib/types.ts re-exports everything here; mobile-app imports it
// via the `@hedgefam/contracts` alias. Add new contract types HERE, not in
// either app.

export type FamRole = "pm" | "rm" | "trader" | "analyst" | "member";

// Display-side tier — collapses the Go-side enum
// ("free" | "fantasy_free" | "fantasy_premium" | "hedge") into the pre-seed
// "free vs paid" axis the web UI cares about. Use `mapTier()` to coerce a raw
// Go tier string into this union before storing it on a UI model.
export type FamTier = "free" | "paid";

// Raw tier strings emitted by the Go fam service (mirror fam.TierFree etc.).
// Useful when the API surface is consumed verbatim before mapping.
export type GoFamTier = "free" | "fantasy_free" | "fantasy_premium" | "hedge";

// Bankroll tier (migration 043) — the portfolio size a Fam or Fantasy Fam
// runs at. '10k' is the free preset; '100k' / '1m' / 'custom' are paid-only.
export type BankrollTier = "10k" | "100k" | "1m" | "custom";

// mapTier collapses any Go-side tier string into the pre-seed display union.
// "free" stays "free"; everything else (fantasy_free, fantasy_premium, hedge,
// or any future paid tier) becomes "paid". Unknown strings default to "free"
// so the UI fails closed (cheaper Fam display) rather than misrepresenting a
// paid tier the web doesn't know about yet.
export function mapTier(goTier: string): FamTier {
	return goTier === "free" ? "free" : "paid";
}

export type IdeaStatus =
	| "open"
	| "passed"
	| "failed"
	| "quorum_failed"
	| "pm_unilateral"
	| "expired"
	| "withdrawn";

export type IdeaDirection = "long" | "short";

export type VoteChoice = "yes" | "no";

// Allowed vote-window options. Mirrors fams.vote_window_hours CHECK in the
// vote service (services-cf/services/vote/src/types.ts). 2h is the free-tier
// default and the only window free users can pick; 24/72/168 are paid-only.
export type VoteWindowHours = 2 | 24 | 72 | 168;

// Server-emitted error codes the Phase 2 UI branches on for inline messaging.
// Mirrors services/internal/vote/types.go and shared httpx codes.
export type VoteServiceErrorCode =
	| "role_cannot_propose"
	| "ticker_below_floor"
	| "unknown_ticker"
	| "idea_not_open"
	| "idea_expired"
	| "not_member"
	| "forbidden"
	| "not_found"
	| "bad_request"
	| "unauthorized"
	| "conflict";

export interface FamSummary {
	id: string;
	slug: string;
	name: string;
	logoUrl: string | null;
	tier: FamTier;
	maxMembers: number;
	memberCount: number;
	role: FamRole;
}

export interface FamMember {
	userId: string;
	xHandle: string | null;
	role: FamRole;
	joinedAt: string;
}

export type FamComposite = Record<string, number>;

export interface FamDetail {
	id: string;
	slug: string;
	name: string;
	logoUrl: string | null;
	tier: FamTier;
	maxMembers: number;
	pmUserId: string;
	composite: FamComposite;
	members: FamMember[];
	openIdeas: IdeaSummary[];
	bankrollTier: BankrollTier;
	bankrollUsd: string; // numeric(20,2) — string on the wire
	createdAt: string;
}

// IdeaSummary is the slim shape rendered in list contexts (Fam viewer's open-
// ideas list). Matches the Go vote service's Idea struct
// (services/internal/vote/types.go) plus a precomputed `consensus` slot the
// fam-service handler hydrates when assembling FamDetail. Numeric percent
// fields (allocationPct, thresholdPct) arrive as JSON numbers — the
// shopspring/decimal default marshals as an unquoted numeric literal — so
// `number` is the right shape on the wire.
export interface IdeaSummary {
	id: string;
	famId: string;
	symbol: string;
	direction: IdeaDirection;
	allocationPct: number;
	thesis: string;
	proposerUserId: string;
	proposerRole: FamRole;
	status: IdeaStatus;
	voteWindowHours: number;
	thresholdPct: number;
	quorumPct: number;
	proposedAt: string;
	resolvesAt: string;
	resolvedAt: string | null;
	consensus: ConsensusResult;
}

// ConsensusResult mirrors services/internal/vote/types.go#ConsensusResult.
// All counts/percentages computed server-side; the UI never recomputes
// pass/fail locally.
export interface ConsensusResult {
	yes: number;
	no: number;
	quorumPct: number;
	yesPct: number;
	membersTotal: number;
}

// IdeaWithConsensus is the response of GET /v1/ideas/{id}. The Idea field is
// the full vote-service Idea row; Consensus is the live tally.
export interface IdeaWithConsensus {
	idea: Idea;
	consensus: ConsensusResult;
}

// Idea is the full vote-service Idea row. IdeaSummary is the narrower shape
// the fam handler embeds in FamDetail; this one is what GET /v1/ideas/{id}
// returns under `idea`.
export interface Idea {
	id: string;
	famId: string;
	proposerUserId: string;
	proposerRole: FamRole;
	symbol: string;
	direction: IdeaDirection;
	allocationPct: number;
	thesis: string;
	thresholdPct: number;
	quorumPct: number;
	voteWindowHours: number;
	status: IdeaStatus;
	proposedAt: string;
	resolvesAt: string;
	resolvedAt?: string;
}

// CreateIdeaRequest mirrors services/internal/vote/types.go#CreateIdeaRequest.
// famId is omitted from the body when POSTing to /v1/fams/{famId}/ideas — the
// URL wins server-side. The web-app's Pages Function proxies the slug-form
// route, so we never send famId in the body either.
export interface CreateIdeaRequest {
	symbol: string;
	direction: IdeaDirection;
	allocationPct: number;
	thesis: string;
	voteWindowHours: VoteWindowHours;
}

// CastVoteRequest body for POST /v1/ideas/{id}/votes.
export interface CastVoteRequest {
	choice: VoteChoice;
}

// ListIdeasResponse — wire shape of GET /v1/fams/{famId}/ideas.
export interface ListIdeasResponse {
	ideas: Idea[];
	limit: number;
	offset: number;
}

export interface ApiError {
	error: {
		code: string;
		message: string;
	};
}

// NOTE: Consolidate shapes below intentionally stay snake_case — they mirror
// Supabase `public.users` row columns and the web-app's own Pages Function
// wire format (not the Go fam/vote services). Changing them would mean
// rewriting consolidate.ts + the SQL adapters in functions/api/auth/.
export interface ConsolidateInput {
	x_user_id?: string;
	x_handle?: string;
	x_verified_email?: string;
	linked_email?: string;
}

export interface ConsolidateOutput {
	user_id: string;
	prompt_merge?: { conflicting_email: string };
}

// --- Fantasy / Power Ranking shapes -----------------------------------------
//
// Mirror the camelCase JSON tags emitted by services/internal/fantasy
// (pre_seed.go). Names follow the Go-side WeeklyLeague / StandingsEntry /
// MePowerRankingResponse exactly so the BFF proxy can pass-through.

export interface WeeklyLeague {
	id: string;
	theme: string;
	themeName: string;
	description: string;
	rosterSize: number;
	weekStartsAt: string;
	weekEndsAt: string;
	status: string;
	entries: number;
}

export interface WeeklyLeaguesResponse {
	weekStartsAt: string;
	weekEndsAt: string;
	leagues: WeeklyLeague[];
}

export interface LineupPick {
	symbol: string;
	allocationPct: number;
}

export interface LineupEntry {
	leagueId: string;
	userId: string;
	theme: string;
	picks: LineupPick[];
	submittedAt: string;
}

// Standings values come over the wire as decimal strings (Go decimal.Decimal
// MarshalJSON emits a quoted string). The UI parses these with parseFloat at
// render time.
export interface LeagueStanding {
	rank: number;
	userId: string;
	username?: string;
	returnPct: string;
	totalValue: string;
}

export interface PowerRow {
	rating: number;
	wins: number;
	losses: number;
	draws: number;
}

export interface StandingsResponse {
	leagueId: string;
	theme?: string;
	entries: LeagueStanding[];
	powerRanking?: Record<string, PowerRow>;
}

// Matches services-cf/services/fantasy's /v1/me/rank handler. Pre-seed shape:
// rating only (the W/L/D record + leaguesPlayed will surface once settle-week
// starts writing them). pmRating defaults to 1000 for users who haven't been
// snapshotted yet; rank + snapshotDate are null in that case.
export interface PowerRankingSummary {
	userId: string;
	pmRating: number;
	rank: number | null;
	snapshotDate: string | null;
}

// --- Fantasy Fams umbrella (Phase 2+) ---------------------------------------
//
// Distinct from the older themed-weekly `WeeklyLeague` shapes above. These
// mirror the backend agent's contract: Fams opt in by `leagueType`, settle
// into per-cadence leaderboards, and roll up to trophies + duels.
//
// `leagueType` enum mirrors the `league_type` Postgres enum from migration
// 034. Post migration 041 the fam-based opt-in surface is gone; Fantasy Fam
// is its own ephemeral entity (see FantasyFam / FantasyFamCadence below).

export type FantasyLeagueType =
	| "fantasy_fams_weekly"
	| "fantasy_fams_quarterly"
	| "fantasy_fams_earnings"
	| "fantasy_fams_duel";

// --- Fantasy Fam entity (migration 041) ------------------------------------

export type FantasyFamCadence = "weekly" | "quarterly" | "earnings";
export type FantasyFamStatus = "active" | "settled" | "abandoned";
// Fantasy Fam roles match the canonical Fam role taxonomy from
// post-seed/Hedge Fam.md (PM / RM / Trader / Analyst / Member). "Captain"
// is reserved as a public honorific for top-5% PM-rated users, not a
// structural role on a Fantasy Fam.
export type FantasyFamRole = FamRole;

export interface FantasyFam {
	id: string;
	name: string;
	slug: string;
	captainUserId: string;
	cadence: FantasyFamCadence;
	periodKey: string;
	status: FantasyFamStatus;
	composite: Record<string, number>;
	returnPct: string | null;
	rank: number | null;
	bankrollTier: BankrollTier;
	bankrollUsd: string; // numeric(20,2) — string on the wire
	createdAt: string;
	settledAt: string | null;
}

export interface FantasyFamMember {
	fantasyFamId: string;
	userId: string;
	role: FantasyFamRole;
	joinedAt: string;
	username: string | null;
}

export interface CreateFantasyFamInput {
	name: string;
	cadence: FantasyFamCadence;
	invites?: string[];
	// Bankroll tier (migration 043). Omitted → server default '10k'.
	// bankrollUsd is required (and only used) when bankrollTier is 'custom'.
	bankrollTier?: BankrollTier;
	bankrollUsd?: number;
	// Earnings cadence only: the ISO week ("YYYY-Www") the Fam competes in.
	// Ignored by the server for weekly + quarterly.
	periodKey?: string;
}

export interface CreateFantasyFamResponse {
	fantasyFam: FantasyFam;
	members: FantasyFamMember[];
}

export interface FantasyFamDetailResponse {
	fantasyFam: FantasyFam;
	members: FantasyFamMember[];
}

export interface ListMyFantasyFamsResponse {
	fantasyFams: FantasyFam[];
}

export interface AddFantasyFamMemberInput {
	userId?: string;
	username?: string;
}

export interface SubmitFantasyFamPicksInput {
	composite: Record<string, number>;
}

// Leaderboard row — matches services-cf/services/fantasy's
// FantasyFamsLeaderboardEntry. Decimal columns come over the wire as quoted
// strings (Postgres NUMERIC → JSON string). famSlug is nullable for Fams
// that haven't been assigned one yet; routes that link off it must guard.
export interface FantasyLeaderboardEntry {
	rank: number;
	famId: string;
	famSlug: string | null;
	famName: string;
	returnPct: string;
	spyDelta?: string | null;
	beatMarket?: boolean;
	// Optional composite-snapshot summary the backend may surface — array of
	// ticker chips that show under the row in the UI.
	tickers?: string[];
	// Optional weekly sparkline history (per-day pct). Only the quarterly
	// response is expected to carry it once we add the chart.
	history?: { t: string; v: number }[];
}

// The three rankable bankroll pools (migration 043). 'custom' Fantasy Fams
// are unranked and never appear on a public leaderboard.
export type LeaderboardBankrollTier = "10k" | "100k" | "1m";

// All three umbrella leaderboards share `FantasyFamsLeaderboardResponse =
// { period, bankroll, entries }`. `period` is the ISO-week ("2026-W22") for
// weekly + earnings, the calendar quarter ("2026Q2") for quarterly.
// `bankroll` echoes the competitive pool the entries belong to.
export interface FantasyFamsLeaderboardResponse {
	period: string;
	bankroll: LeaderboardBankrollTier;
	entries: FantasyLeaderboardEntry[];
}

export type WeeklyLeaderboardResponse = FantasyFamsLeaderboardResponse;
export type QuarterlyLeaderboardResponse = FantasyFamsLeaderboardResponse;
export type EarningsLeaderboardResponse = FantasyFamsLeaderboardResponse;

// --- Duels ------------------------------------------------------------------

export type DuelCadence = "weekly" | "monthly" | "quarterly";
export type DuelStatus = "forming" | "active" | "completed" | "declined" | "expired";

export interface DuelFamSide {
	famId: string;
	famSlug: string;
	famName: string;
	pmUserId?: string;
	returnPct?: number;
}

export interface Duel {
	id: string;
	challenger: DuelFamSide;
	opponent: DuelFamSide;
	cadence: DuelCadence;
	status: DuelStatus;
	wager?: string | null;
	createdAt: string;
	acceptedAt?: string | null;
	startedAt?: string | null;
	endsAt?: string | null;
	winnerFamId?: string | null;
}

export interface DuelDetailResponse {
	duel: Duel;
}

export interface RecentDuelsResponse {
	duels: Duel[];
}

export interface CreateDuelRequest {
	opponentFamSlug: string;
	cadence: DuelCadence;
	wager?: string;
}

export interface CreateDuelResponse {
	duelId: string;
	status: DuelStatus;
}

export interface DuelMutationResponse {
	ok: boolean;
	startedAt?: string | null;
}

// --- Trophies ---------------------------------------------------------------

export type TrophyType =
	| "weekly_winner"
	| "quarter_champ"
	| "earnings_king"
	| "duel_winner"
	| "beat_the_market"
	| "top_decile_streak";

export interface Trophy {
	type: TrophyType;
	season: string | null;
	awardedAt: string;
	metadata?: Record<string, unknown>;
}

export interface FamTrophiesResponse {
	trophies: Trophy[];
}

// --- Fam settings request shapes --------------------------------------------

export interface UpdateMemberRoleRequest {
	role: FamRole;
}

export interface InviteMemberRequest {
	xHandle?: string;
	userId?: string;
	role?: FamRole;
}

export interface UpdateFamRequest {
	name?: string;
	logoUrl?: string;
}

// --- Stripe / subscription wire shapes -------------------------------------
//
// Mirror of the `subscriptions` table (migration 011, columns are
// snake_case in Postgres). We expose camelCase to the UI per the rest of
// this file. The Pages Function in functions/api/me/subscription.ts is
// responsible for the snake→camel mapping.
//
// `status` is whatever Stripe emits (active, trialing, past_due, canceled,
// incomplete, incomplete_expired, unpaid, paused). The literal `none` is a
// sentinel the API returns when the user has no subscription row at all —
// it lets the UI render the "free tier" state without a separate 404.

export type SubscriptionStatus =
	| "none"
	| "active"
	| "trialing"
	| "past_due"
	| "canceled"
	| "incomplete"
	| "incomplete_expired"
	| "unpaid"
	| "paused";

export type BillingCycle = "monthly" | "annual";

export interface SubscriptionRow {
	id: string | null;
	userId: string | null;
	product: string | null;
	stripeCustomerId: string | null;
	stripeSubscriptionId: string | null;
	status: SubscriptionStatus;
	billingCycle: BillingCycle | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
	createdAt: string | null;
	updatedAt: string | null;
}

// Checkout body matches the billing Worker's POST /v1/stripe/checkout
// (services-cf/services/billing/src/index.ts). The Worker maps plan →
// Stripe price id server-side so we never ship price strings to the
// browser — that lets us rotate prices without a frontend redeploy.
export interface CheckoutRequest {
	plan: BillingCycle;
}

export interface CheckoutResponse {
	checkoutUrl: string;
	sessionId: string;
}

export interface PortalResponse {
	portalUrl: string;
}
